import React, { useState, useEffect, useCallback } from 'react';
import { TextField, Text, Banner, BlockStack } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useAuth, needsPhoneNumber } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { apiSetPhoneNumber, apiSubmitVerification } from '../services/api';
import { usePageT } from '../i18n/createT';
import { COMPLETE_PROFILE_T } from '../i18n/completeProfileT';
import { isValidInternationalPhone, isValidPhoneInput, toInternationalPhone } from '../utils/phone';
import kioLogo from '../assets/kio-logo.svg';
import { SPINNER_CSS, BlackBtn, LangSwitcher, AuthLeftPanel, PhoneField } from '../components/AuthShared';

/** apiSetPhoneNumber error message → translation key. */
const SAVE_ERROR_KEYS = {
  phone_invalid: 'errorInvalid',
  phone_taken: 'errorTaken',
  sms_failed: 'errorSms',
};

const linkBtn = {
  background: 'none', border: 'none', color: '#111', cursor: 'pointer', fontSize: 13,
  fontWeight: 500, padding: 0, textDecoration: 'underline', textUnderlineOffset: 3,
};

/**
 * Blocking step for an account without a phone number (Google sign-up):
 * 1. ask the number and save it (kio-api texts a code to it);
 * 2. offer to type that code right away, or to verify later (the dashboard's
 *    ContactVerificationBanner keeps reminding).
 * `codeSentTo` is owned by PhoneRequiredGate so step 2 survives the user refresh.
 */
export default function CompleteProfile({ codeSentTo, onCodeSent, onDone }) {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const t = usePageT(COMPLETE_PROFILE_T, lang);
  const isRtl = lang === 'ar';

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [dialCode, setDialCode] = useState('+213');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleSave = useCallback(async () => {
    if (busy) return;
    setError('');
    const number = toInternationalPhone(dialCode, phone);
    if (!isValidPhoneInput(phone) || !isValidInternationalPhone(number)) {
      setError(t.errorInvalid);
      return;
    }
    setBusy(true);
    try {
      await apiSetPhoneNumber(number);
      onCodeSent(number);
      await refreshUser().catch(() => { /* the gate keeps the code step open */ });
      setBusy(false);
    } catch (e) {
      if (e.message === 'sms_failed') {
        // The SMS provider failed: continue only if kio-api kept the number anyway.
        const fresh = await refreshUser().catch(() => null);
        if (fresh && !needsPhoneNumber(fresh)) {
          onDone();
          return;
        }
      }
      setError(t[SAVE_ERROR_KEYS[e.message]] || t.errorGeneric);
      setBusy(false);
    }
  }, [busy, dialCode, phone, t, onCodeSent, onDone, refreshUser]);

  const handleVerify = useCallback(async () => {
    if (busy) return;
    setError('');
    if (!code.trim()) {
      setError(t.codeEmpty);
      return;
    }
    setBusy(true);
    try {
      await apiSubmitVerification({ code: code.trim(), phoneNumber: codeSentTo, contactMode: 'phone' });
      await refreshUser().catch(() => { /* banner state refreshes on next load */ });
      onDone();
    } catch (e) {
      setError(/code incorrect/i.test(e.message) ? t.codeIncorrect : t.verifyFailed);
      setBusy(false);
    }
  }, [busy, code, codeSentTo, t, refreshUser, onDone]);

  const handleVerifyLater = useCallback(async () => {
    if (busy) return;
    // The refresh right after saving may have failed: the gate needs the pending number.
    if (needsPhoneNumber(user)) {
      setBusy(true);
      await refreshUser().catch(() => {});
    }
    onDone();
  }, [busy, user, refreshUser, onDone]);

  const handleLogout = useCallback(() => {
    onDone();
    logout();
    navigate('/login', { replace: true });
  }, [onDone, logout, navigate]);

  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter') return;
    if (codeSentTo) handleVerify();
    else handleSave();
  }, [codeSentTo, handleVerify, handleSave]);

  return (
    <>
      <style>{SPINNER_CSS}</style>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }} onKeyDown={handleKeyDown}>
        {!isMobile && <AuthLeftPanel lang={lang} setLang={setLang} isRtl={isRtl} tagline={t.tagline} />}

        <div style={{ height: '100vh', background: '#fff', display: 'flex', flexDirection: 'column', direction: isRtl ? 'rtl' : 'ltr', overflow: 'hidden', flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 32px', borderBottom: '1px solid #f3f4f6', flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleLogout}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#616161', fontWeight: 500, padding: '4px 8px' }}
            >
              {t.logout}
            </button>
            <LangSwitcher lang={lang} setLang={setLang} light={false} />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '32px 24px' : '48px 56px', maxWidth: 480, width: '100%', margin: '0 auto', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ marginBottom: 24 }}>
              <img src={kioLogo} alt="KIO" style={{ height: isMobile ? 40 : 30, display: 'block' }} />
            </div>

            <div style={{ marginBottom: 28 }}>
              <Text variant="headingXl" as="h1">{codeSentTo ? t.codeTitle : t.title}</Text>
              <div style={{ marginTop: 6 }}>
                {codeSentTo ? (
                  <Text variant="bodyMd" tone="subdued">{t.codeDesc} <strong dir="ltr">{codeSentTo}</strong></Text>
                ) : (
                  <Text variant="bodyMd" tone="subdued">{t.reason}</Text>
                )}
              </div>
            </div>

            <BlockStack gap="400">
              {error && (
                <div role="alert">
                  <Banner tone="critical" onDismiss={() => setError('')}>{error}</Banner>
                </div>
              )}

              {codeSentTo ? (
                <>
                  <TextField
                    label={t.codeLabel}
                    type="text"
                    value={code}
                    onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
                    placeholder={t.codePlaceholder}
                    autoFocus
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                  <BlackBtn onClick={handleVerify} loading={busy}>{t.verify}</BlackBtn>
                  <div style={{ textAlign: 'center' }}>
                    <button type="button" style={linkBtn} disabled={busy} onClick={handleVerifyLater}>
                      {t.verifyLater}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <PhoneField
                    label={t.phoneLabel}
                    dialCode={dialCode}
                    onDialChange={setDialCode}
                    value={phone}
                    onChange={setPhone}
                    placeholder={t.phonePlaceholder}
                    isRtl={isRtl}
                  />
                  <BlackBtn onClick={handleSave} loading={busy}>{t.save}</BlackBtn>
                </>
              )}
            </BlockStack>
          </div>
        </div>
      </div>
    </>
  );
}
